import React, { useState, useEffect } from 'react';
import './AccessibilityEnhancements.css';

interface AccessibilitySettings {
    highContrast: boolean;
    fontSize: 'small' | 'medium' | 'large' | 'extra-large';
    reducedMotion: boolean;
    screenReader: boolean;
    keyboardNavigation: boolean;
    focusVisible: boolean;
}

const AccessibilityEnhancements: React.FC = () => {
    const [settings, setSettings] = useState<AccessibilitySettings>({
        highContrast: false,
        fontSize: 'medium',
        reducedMotion: false,
        screenReader: false,
        keyboardNavigation: false,
        focusVisible: true
    });

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        // Detect screen reader usage
        const detectScreenReader = () => {
            const isScreenReader = 
                window.speechSynthesis && 
                window.speechSynthesis.getVoices().length > 0 &&
                (window as any).speechSynthesis.onvoiceschanged !== undefined;
            
            setSettings(prev => ({ ...prev, screenReader: isScreenReader }));
        };

        detectScreenReader();

        // Detect reduced motion preference
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setSettings(prev => ({ ...prev, reducedMotion: mediaQuery.matches }));

        const handleChange = (e: MediaQueryListEvent) => {
            setSettings(prev => ({ ...prev, reducedMotion: e.matches }));
        };

        mediaQuery.addEventListener('change', handleChange);

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    useEffect(() => {
        // Apply accessibility settings to document
        const root = document.documentElement;
        
        // High contrast
        if (settings.highContrast) {
            root.classList.add('high-contrast');
        } else {
            root.classList.remove('high-contrast');
        }

        // Font size
        root.classList.remove('font-small', 'font-medium', 'font-large', 'font-extra-large');
        root.classList.add(`font-${settings.fontSize}`);

        // Reduced motion
        if (settings.reducedMotion) {
            root.classList.add('reduced-motion');
        } else {
            root.classList.remove('reduced-motion');
        }

        // Focus visible
        if (settings.focusVisible) {
            root.classList.add('focus-visible');
        } else {
            root.classList.remove('focus-visible');
        }

        // Keyboard navigation
        if (settings.keyboardNavigation) {
            root.classList.add('keyboard-navigation');
        } else {
            root.classList.remove('keyboard-navigation');
        }

    }, [settings]);

    const updateSetting = <K extends keyof AccessibilitySettings>(
        key: K, 
        value: AccessibilitySettings[K]
    ) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const resetSettings = () => {
        setSettings({
            highContrast: false,
            fontSize: 'medium',
            reducedMotion: false,
            screenReader: false,
            keyboardNavigation: false,
            focusVisible: true
        });
    };

    return (
        <>
            {/* Accessibility Toggle Button */}
            <button
                className="accessibility-toggle"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Open accessibility settings"
                aria-expanded={isOpen}
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
                </svg>
            </button>

            {/* Accessibility Panel */}
            {isOpen && (
                <div className="accessibility-panel" role="dialog" aria-labelledby="accessibility-title">
                    <div className="accessibility-header">
                        <h3 id="accessibility-title">Accessibility Settings</h3>
                        <button
                            className="close-button"
                            onClick={() => setIsOpen(false)}
                            aria-label="Close accessibility settings"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>

                    <div className="accessibility-content">
                        {/* High Contrast */}
                        <div className="setting-group">
                            <label className="setting-label">
                                <input
                                    type="checkbox"
                                    checked={settings.highContrast}
                                    onChange={(e) => updateSetting('highContrast', e.target.checked)}
                                />
                                <span className="checkmark"></span>
                                High Contrast
                            </label>
                            <p className="setting-description">
                                Increases contrast for better visibility
                            </p>
                        </div>

                        {/* Font Size */}
                        <div className="setting-group">
                            <label className="setting-label">Font Size</label>
                            <div className="font-size-options">
                                {(['small', 'medium', 'large', 'extra-large'] as const).map(size => (
                                    <button
                                        key={size}
                                        className={`font-option ${settings.fontSize === size ? 'active' : ''}`}
                                        onClick={() => updateSetting('fontSize', size)}
                                        aria-pressed={settings.fontSize === size}
                                    >
                                        Aa
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Reduced Motion */}
                        <div className="setting-group">
                            <label className="setting-label">
                                <input
                                    type="checkbox"
                                    checked={settings.reducedMotion}
                                    onChange={(e) => updateSetting('reducedMotion', e.target.checked)}
                                />
                                <span className="checkmark"></span>
                                Reduce Motion
                            </label>
                            <p className="setting-description">
                                Reduces animations and transitions
                            </p>
                        </div>

                        {/* Keyboard Navigation */}
                        <div className="setting-group">
                            <label className="setting-label">
                                <input
                                    type="checkbox"
                                    checked={settings.keyboardNavigation}
                                    onChange={(e) => updateSetting('keyboardNavigation', e.target.checked)}
                                />
                                <span className="checkmark"></span>
                                Enhanced Keyboard Navigation
                            </label>
                            <p className="setting-description">
                                Improves keyboard navigation experience
                            </p>
                        </div>

                        {/* Focus Visible */}
                        <div className="setting-group">
                            <label className="setting-label">
                                <input
                                    type="checkbox"
                                    checked={settings.focusVisible}
                                    onChange={(e) => updateSetting('focusVisible', e.target.checked)}
                                />
                                <span className="checkmark"></span>
                                Enhanced Focus Indicators
                            </label>
                            <p className="setting-description">
                                Makes focus indicators more visible
                            </p>
                        </div>

                        {/* Screen Reader Info */}
                        {settings.screenReader && (
                            <div className="screen-reader-info">
                                <span className="info-icon">ℹ️</span>
                                Screen reader detected - enhanced accessibility features enabled
                            </div>
                        )}

                        {/* Reset Button */}
                        <div className="accessibility-actions">
                            <button
                                className="reset-button"
                                onClick={resetSettings}
                            >
                                Reset to Defaults
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AccessibilityEnhancements;
